import React, { lazy, useState, useCallback, useContext, useEffect } from 'react';
import { useLocation, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { retry } from '../../utils/retry';
import I18nContext from '../../contexts/i18n';
import Media from 'react-media';
import {
  getNavbarZIndex,
  getMobileNavbar2HeightPx,
  getDesktopNavbarHeightPx,
  getNavbarDesktopConditionMediaQuery
} from '../../utils/navbar_size';
import CartContext from '../../contexts/cart';
import { useUser } from '../../contexts/user';
import SecureCheckoutNav from './common/secure_navbar';
import { isRestoringListScroll } from '../../utils/list_restoration';

const DesktopNavbarTopV2 = lazy(() => retry(() => import('./desktop/navbar_top_v2')));
const DesktopNavbarBotV2 = lazy(() => retry(() => import('./desktop/navbar_bot_v2')));
const MobileNavbarV2 = lazy(() => retry(() => import('./mobile/navbar_menu_v2')));
const MobileNavbarMenu = lazy(() => retry(() => import('./mobile/navbar_menu')));

const SCROLL_DOWN_STREAK_TO_TOGGLE_NAVBAR = 64;
const SCROLL_UP_STREAK_TO_TOGGLE_NAVBAR = 1;
const MIN_SCROLL_TO_HIDE = 128;

const minimal = new URLSearchParams(window.location.search).get('minimal');

// Careful not to put overflow: hidden in here, it would cut out search results.
// Hide the navbar when minimal is true (webview mode).
const FixedNavbarWrapper = styled.nav.attrs(() => ({ 'data-fixed-navbar': 'true' }))`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.navHeight};
  background: #fff;
  z-index: ${props => getNavbarZIndex()};
  transition: all 200ms ease-in-out;
  transform: translate3d(0, ${props => props.hiding && !props.checkoutRoute ? `-${props.navHeight}` : '0'}, 0);
  display: ${minimal === 'true' ? 'none' : 'flex'};
  flex-direction: column;
  align-items: center;
`;

const FixedNavbar = () => {
  const { user, loggedIn } = useUser();
  const cart = useContext(CartContext);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hidingNavbar, setHidingNavbar] = useState(false);
  const i18nSettings = useContext(I18nContext);
  const rtl = i18nSettings.isLocaleRTL();
  const openFn = useCallback(() => setOpen(true), []);
  const closeFn = useCallback(() => {
    setOpen(false);
    if (document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }, []);

  const locationPathname = location.pathname;
  const checkoutRoute = (locationPathname === '/checkout');

  const toggleFn = useCallback(() => {
    if (open) {
      closeFn();
    } else {
      openFn();
    }
  }, [open, openFn, closeFn]);

  // When someone navigates away, we should unhide the navbar.
  useEffect(() => {
    setHidingNavbar(false);
  }, [locationPathname]);

  // When someone scrolls down sufficiently, or up sufficiently,
  // we should toggle the visibility of the navbar.
  useEffect(() => {
    let prevScrollTop = null;
    let streakSize = null;
    let streakSign = null;
    const onScroll = (e) => {
      const currentScrollTop = document.body.scrollTop;
      if (isRestoringListScroll()) {
        prevScrollTop = currentScrollTop;
        return;
      }
      const delta = currentScrollTop - (prevScrollTop == null ? currentScrollTop : prevScrollTop);
      prevScrollTop = currentScrollTop;
      if (delta === 0) return;
      const deltaSign = delta < 0 ? -1 : +1;
      if (deltaSign === streakSign) {
        streakSize += Math.abs(delta);
      } else {
        streakSize = Math.abs(delta);
        streakSign = deltaSign;
      }
      if (currentScrollTop < MIN_SCROLL_TO_HIDE) {
        setHidingNavbar(false);
      } else {
        if (streakSign > 0 && streakSize > SCROLL_DOWN_STREAK_TO_TOGGLE_NAVBAR) {
          setHidingNavbar(true);
        } else if (streakSign < 0 && streakSize > SCROLL_UP_STREAK_TO_TOGGLE_NAVBAR) {
          setHidingNavbar(false);
        }
      }
    };
    document.body.addEventListener('scroll', onScroll);
    return () => document.body.removeEventListener('scroll', onScroll);
  }, [checkoutRoute]);

  useEffect(() => {
    // Hide the navbar for some components.
    if (locationPathname.includes('/live-stream')) {
      setHidingNavbar(true);
    }
  }, [checkoutRoute, locationPathname]);

  return (
    <Media query={getNavbarDesktopConditionMediaQuery()}>
      {desktop => desktop
        ? <FixedNavbarWrapper hiding={!checkoutRoute && hidingNavbar} checkoutRoute={checkoutRoute}
            navHeight={checkoutRoute ? 'auto' : (getDesktopNavbarHeightPx() + 'px')}>
            {checkoutRoute
              ? <SecureCheckoutNav desktop={desktop} />
              : <>
                  <DesktopNavbarTopV2 user={user} cart={cart} loggedIn={loggedIn} />
                  <DesktopNavbarBotV2 user={user} cart={cart} loggedIn={loggedIn} />
                </>}
          </FixedNavbarWrapper>
        : <FixedNavbarWrapper hiding={!checkoutRoute && hidingNavbar} navHeight={getMobileNavbar2HeightPx() + 'px'}
            checkoutRoute={checkoutRoute}>
            {checkoutRoute
              ? <SecureCheckoutNav />
              : <>
                  <DesktopNavbarTopV2 mobile user={user} cart={cart} loggedIn={loggedIn} height='45' />
                  <MobileNavbarV2 onClickBurgers={toggleFn} cartSize={cart.size({ useEphemeral: false })} user={user}
                    loggedIn={loggedIn} />
                  <MobileNavbarMenu open={open} closeMenu={closeFn} cart={cart} user={user}
                    position={rtl ? 'left' : 'right'} />
                </>}
          </FixedNavbarWrapper>}
    </Media>
  );
};

export default withRouter(FixedNavbar);
